/**
 * Time formatting utilities
 * Shared relative-time ("x ago") formatting so call sites don't each hand-roll
 * their own thresholds.
 *
 * @module timeFormat
 */

/**
 * Normalize a Date | ISO string | epoch-ms into epoch milliseconds.
 *
 * @param {Date|string|number|null|undefined} input
 * @returns {number} epoch ms, or NaN when the input is missing/unparseable
 */
function toMillis(input) {
  if (input === null || input === undefined) return NaN;
  if (input instanceof Date) return input.getTime();
  if (typeof input === 'number') return input;
  return new Date(input).getTime();
}

/**
 * Format a timestamp as a short relative "time ago" string.
 *
 * Two granularities share the same day/month/year tail:
 *  - `'time'` (default): sub-day precision — `just now`, `5 min ago`, `2 hr ago`,
 *    then `yesterday` / `N days ago` / `N months ago` / `N years ago`. Use this
 *    where recency matters (e.g. the dashboard resume card).
 *  - `'day'`: day precision — `today`, `yesterday`, `N days ago`, `N months ago`,
 *    `N years ago`. Use this for last-activity labels where minute precision is
 *    noise.
 *
 * Future timestamps (clock skew) clamp to the most-recent bucket.
 *
 * @param {Date|string|number|null|undefined} input - Timestamp to format
 * @param {Object} [opts]
 * @param {'time'|'day'} [opts.granularity='time'] - Precision below one day
 * @param {string|null} [opts.emptyLabel=null] - Returned when input is missing/invalid
 * @returns {string|null} Relative-time string, or `emptyLabel` when unavailable
 */
export function formatTimeAgo(input, { granularity = 'time', emptyLabel = null } = {}) {
  const then = toMillis(input);
  if (Number.isNaN(then)) return emptyLabel;

  const diffMs = Math.max(0, Date.now() - then);

  if (granularity === 'time') {
    if (diffMs < 60_000) return 'just now';
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
  }

  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}

/**
 * Compact relative date for a list row's right side ("10 hours ago", "2 days
 * ago", then a short date once it's older than a week). This is the exact
 * format the /notes and /search rows share — kept distinct from
 * `formatTimeAgo` (which uses abbreviated "min"/"hr" units and a different
 * day/month/year tail). Empty string on missing/unparseable input.
 *
 * @param {string|Date|number|null|undefined} iso - Timestamp to format
 * @returns {string} Relative label, short date, or '' when unavailable
 */
export function formatRelativeDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const opts = { month: 'short', day: 'numeric' };
  if (date.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString('en-US', opts);
}
